import {
  IonIcon,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonToggle,
} from '@ionic/react';
import { folderOpenOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import {
  parseScheduleConfig,
  type AutomationScheduleConfig,
} from '../../automation/scheduleConfig';
import {
  formatExtensionValue,
  getTriggerStepLabel,
} from '../../automation/workflowSummary';
import type { WorkflowValidationIssue } from '../../automation/workflowGraph';
import {
  AUTOMATION_SCHEDULE_FREQUENCIES,
  AUTOMATION_TRIGGER_LABELS,
  AUTOMATION_WEEKDAYS,
  type AutomationScheduleFrequency,
  type AutomationTrigger,
  type AutomationTriggerType,
} from '../../types/AutomationWorkflow.types';

export const WorkflowTriggerCard: React.FC<{
  trigger: AutomationTrigger;
  extensions: string[];
  issues: WorkflowValidationIssue[];
  onTriggerChange: (trigger: AutomationTrigger) => void;
  onPickFolder: () => void;
}> = ({ trigger, extensions, issues, onTriggerChange, onPickFolder }) => {
  const [editingExtensions, setEditingExtensions] = useState(false);

  const patchConfig = (patch: Record<string, unknown>) =>
    onTriggerChange({
      ...trigger,
      config: { ...trigger.config, ...patch },
    });

  const handleTriggerTypeChange = (nextType: AutomationTriggerType) => {
    if (nextType === trigger.type) return;
    const nextConfig: Record<string, unknown> =
      nextType === 'file_added'
        ? {
            extensions:
              trigger.config.extensions ?? 'mp3, wav, flac, aiff, m4a',
            includeSubfolders: trigger.config.includeSubfolders !== false,
            folderPath: trigger.config.folderPath ?? '',
          }
        : nextType === 'schedule'
          ? { ...parseScheduleConfig(trigger.config) }
          : {};
    onTriggerChange({
      type: nextType,
      config: nextConfig,
    });
  };

  return (
    <article
      className={`automation-step-card automation-step-card-trigger${issues.length ? ' invalid' : ''}`}
    >
      <div className="automation-trigger-card-inner">
        <div className="automation-trigger-card-head">
          <span className="automation-step-kicker-when">WHEN</span>
          <h3>{getTriggerStepLabel(trigger)}</h3>
          <span className="automation-trigger-head-spacer" aria-hidden />
          <div className="automation-change-trigger-wrap">
            <span>Change trigger</span>
            <IonSelect
              interface="popover"
              aria-label="Change trigger"
              value={trigger.type}
              className="automation-change-trigger-select"
              onIonChange={(event) =>
                handleTriggerTypeChange(
                  event.detail.value as AutomationTriggerType,
                )
              }
            >
              {Object.entries(AUTOMATION_TRIGGER_LABELS).map(
                ([value, label]) => (
                  <IonSelectOption key={value} value={value}>
                    {label}
                  </IonSelectOption>
                ),
              )}
            </IonSelect>
          </div>
        </div>

        {trigger.type === 'file_added' && (
          <div className="automation-trigger-fields">
            <button
              type="button"
              className="automation-trigger-field automation-trigger-folder-field"
              onClick={onPickFolder}
            >
              <IonIcon icon={folderOpenOutline} aria-hidden />
              <span className="automation-trigger-path">
                {String(trigger.config.folderPath ?? '') ||
                  'Choose a folder to watch'}
              </span>
              <span className="automation-trigger-browse">Browse</span>
            </button>
            <div className="automation-trigger-field automation-trigger-ext-field">
              <span className="automation-trigger-extensions-label">Only</span>
              {editingExtensions ? (
                <IonInput
                  className="automation-trigger-extensions-input"
                  value={String(
                    trigger.config.extensions ??
                      formatExtensionValue(extensions),
                  )}
                  onIonInput={(event) =>
                    patchConfig({ extensions: event.detail.value ?? '' })
                  }
                  onIonBlur={() => setEditingExtensions(false)}
                />
              ) : (
                <button
                  type="button"
                  className="automation-trigger-ext-values"
                  onClick={() => setEditingExtensions(true)}
                >
                  {extensions.map((ext) => `.${ext}`).join(' · ')}
                </button>
              )}
            </div>
            <div className="automation-trigger-field automation-trigger-subfolder-field">
              <span>Include subfolders</span>
              <IonToggle
                checked={trigger.config.includeSubfolders !== false}
                onIonChange={(event) =>
                  patchConfig({ includeSubfolders: event.detail.checked })
                }
              />
            </div>
          </div>
        )}

        {trigger.type === 'schedule' && (
          <ScheduleTriggerFields
            config={parseScheduleConfig(trigger.config)}
            onChange={patchConfig}
          />
        )}
      </div>

      {issues.length > 0 && (
        <p className="automation-field-error">{issues[0].message}</p>
      )}
    </article>
  );
};

const ScheduleTriggerFields: React.FC<{
  config: AutomationScheduleConfig;
  onChange: (patch: Record<string, unknown>) => void;
}> = ({ config, onChange }) => (
  <div className="automation-trigger-fields">
    <div className="automation-trigger-field">
      <span className="automation-trigger-extensions-label">Repeat</span>
      <IonSelect
        interface="popover"
        aria-label="Repeat"
        value={config.frequency}
        onIonChange={(event) =>
          onChange({
            frequency: event.detail.value as AutomationScheduleFrequency,
          })
        }
      >
        {AUTOMATION_SCHEDULE_FREQUENCIES.map((option) => (
          <IonSelectOption key={option.value} value={option.value}>
            {option.label}
          </IonSelectOption>
        ))}
      </IonSelect>
    </div>

    {config.frequency === 'weekly' && (
      <div className="automation-trigger-field">
        <span className="automation-trigger-extensions-label">On</span>
        <IonSelect
          interface="popover"
          aria-label="Day of week"
          value={config.dayOfWeek}
          onIonChange={(event) => onChange({ dayOfWeek: event.detail.value })}
        >
          {AUTOMATION_WEEKDAYS.map((day, index) => (
            <IonSelectOption key={day} value={index}>
              {day}
            </IonSelectOption>
          ))}
        </IonSelect>
      </div>
    )}

    <div className="automation-trigger-field">
      <span className="automation-trigger-extensions-label">
        {config.frequency === 'hourly' ? 'At minute' : 'At'}
      </span>
      {config.frequency !== 'hourly' && (
        <IonInput
          className="automation-trigger-time-input"
          aria-label="Hour"
          type="number"
          min={0}
          max={23}
          value={config.hour}
          onIonInput={(event) => onChange({ hour: Number(event.detail.value) })}
        />
      )}
      <IonInput
        className="automation-trigger-time-input"
        aria-label="Minute"
        type="number"
        min={0}
        max={59}
        value={config.minute}
        onIonInput={(event) => onChange({ minute: Number(event.detail.value) })}
      />
    </div>
  </div>
);
